import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, LoaderCircle, Send } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { CvUpload } from './CvUpload';
import { formFields, submitLabels } from '../../data/formFields';
import { useEnquiry } from '../../hooks/useEnquiry';

const controlClass = 'w-full h-auto px-4 py-3 bg-white border border-gray-300 rounded-md focus-visible:ring-[#E30022] text-base';

export const EnquiryForm = ({ kind, service }) => {
  const { submit, busy, error, receipt } = useEnquiry(kind);
  const [cv, setCv] = useState(null);
  const [fileError, setFileError] = useState('');
  const status = useRef(null);
  useEffect(() => { if (receipt) status.current?.focus(); }, [receipt]);
  const onSubmit = (event) => {
    event.preventDefault();
    setFileError('');
    if (kind === 'cv' && !cv) { setFileError('Veuillez joindre votre CV avant d’envoyer votre candidature.'); return; }
    const values = Object.fromEntries(new FormData(event.currentTarget));
    values.consent = values.consent === 'on';
    if (service) values.service = service;
    submit(values, cv);
  };
  if (receipt) return (
    <section ref={status} tabIndex={-1} role="status" data-testid={`${kind}-success`} className="py-8 text-center outline-none">
      <CheckCircle aria-hidden="true" className="h-14 w-14 text-green-600 mx-auto mb-5" />
      <h2 data-testid={`${kind}-success-title`} className="text-lg font-serif text-gray-800 mb-4">{kind === 'cv' ? 'Votre candidature est enregistrée' : 'Votre demande est enregistrée'}</h2>
      <p data-testid={`${kind}-success-message`} className="text-gray-600 mb-4">{kind === 'study' ? 'Votre demande d’étude a été prise en compte. Notre équipe vous répondra par e-mail.' : 'Merci de votre confiance. Notre équipe prendra connaissance de votre demande.'}</p>
      <p data-testid={`${kind}-receipt`} className="text-sm text-gray-500 break-all mb-6">Référence : {receipt.id}</p>
      <Link data-testid={`${kind}-success-home`} to="/" className="text-[#E30022] hover:underline">Retour à l’accueil</Link>
    </section>
  );
  return (
    <form onSubmit={onSubmit} data-testid={`${kind}-form`} className="space-y-6" aria-busy={busy}>
      <fieldset disabled={busy} className="min-w-0 space-y-6">
        <div className="grid sm:grid-cols-2 gap-5">
          {formFields[kind].map(field => {
            const id = `${kind}-${field.name}`;
            const attributes = { id, name: field.name, required: field.required, 'data-testid': id, className: controlClass };
            return <div key={field.name} className={field.type === 'textarea' ? 'sm:col-span-2' : 'min-w-0'}>
              <Label htmlFor={id} className="block text-sm text-gray-700 mb-2">{field.label}{field.required ? ' *' : ''}</Label>
              {field.type === 'select' ? <select {...attributes} defaultValue=""><option value="">Sélectionnez</option>{field.options.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select>
                : field.type === 'textarea' ? <Textarea {...attributes} maxLength={field.maxLength} rows={4} />
                  : <Input {...attributes} type={field.type} maxLength={field.maxLength} minLength={field.type === 'tel' && field.required ? 6 : undefined} autoComplete={{ firstName: 'given-name', lastName: 'family-name', email: 'email', phone: 'tel', companyName: 'organization', contactName: 'name' }[field.name]} />}
            </div>;
          })}
        </div>
        {kind === 'cv' && <CvUpload file={cv} onChange={file => { setCv(file); setFileError(''); }} disabled={busy} />}
        <div aria-hidden="true" className="hidden"><label htmlFor={`${kind}-website`}>Site web</label><input id={`${kind}-website`} name="website" data-testid={`${kind}-website`} tabIndex={-1} autoComplete="off" /></div>
        <div className="flex items-start gap-3">
          <input id={`${kind}-consent`} name="consent" data-testid={`${kind}-consent`} type="checkbox" required className="mt-1 h-4 w-4 shrink-0 accent-[#E30022]" />
          <Label htmlFor={`${kind}-consent`} className="text-sm font-normal leading-relaxed text-gray-600">J’accepte le traitement de mes données pour répondre à cette demande, conformément à la <Link to="/politique-confidentialite" target="_blank" rel="noreferrer" data-testid={`${kind}-privacy-link`} className="text-[#E30022] underline">politique de confidentialité</Link>.</Label>
        </div>
      </fieldset>
      {(error || fileError) && <p role="alert" data-testid={`${kind}-error`} className="text-red-700 bg-red-50 border-l-4 border-red-600 p-4 text-sm">{fileError || error}</p>}
      <Button type="submit" data-testid={`${kind}-submit`} disabled={busy} className="w-full h-auto min-h-12 whitespace-normal bg-[#E30022] hover:bg-[#c7001d] text-white py-4 px-4 text-base transition-colors">
        {busy ? <LoaderCircle aria-hidden="true" className="h-5 w-5 animate-spin shrink-0" /> : <Send aria-hidden="true" className="h-5 w-5 shrink-0" />}
        {busy ? 'Enregistrement en cours…' : submitLabels[kind]}
      </Button>
    </form>
  );
};